import { html, useState } from '/vendor/htm-preact.module.js';
import { post, del } from '/api.js';
import { Chat } from '/chat.js';

/**
 * A venue's noticeboard.
 *
 * Venue-wide rather than per-circle: a landlord announcing a quiz night is
 * talking to everybody who drinks there, and posting the same thing into four
 * circles is how it ends up said three times and wrong once.
 *
 * Two kinds. A NOTE is something to know — the table is being recovered on
 * Saturday. An EVENT is something to turn up to, so it carries a when, and the
 * date is what the card leads with. They are one feature because the
 * difference between them is one field, and two boards would mean two places
 * to look.
 *
 * Every notice can be talked about. The thread is chat, in its own room keyed
 * on the bulletin — so the quiz night and the broken table are two
 * conversations, and both go with the day while the notice itself stays.
 */
export function Bulletins({ venueId, board, mayPost, me, onChanged }) {
  const [writing, setWriting] = useState(false);
  const [open, setOpen] = useState(null);
  const [error, setError] = useState('');

  const at = `/api/venues/${encodeURIComponent(venueId)}/bulletins`;

  const put = async (notice) => {
    setError('');
    try {
      await post(at, notice);
      setWriting(false);
      await onChanged();
    } catch (e) { setError(e.detail || e.message); }
  };

  const take = async (id) => {
    setError('');
    try {
      await del(`${at}/${encodeURIComponent(id)}`);
      if (open === id) setOpen(null);
      await onChanged();
    } catch (e) { setError(e.detail || e.message); }
  };

  return html`
    <div class="stack" data-testid="venue-bulletins">
      <div class="rowbetween">
        <label>Noticeboard</label>
        ${mayPost && html`
          <button class="secondary" style="width:auto"
                  data-testid="bulletin-write-toggle"
                  onClick=${() => setWriting(!writing)}>
            ${writing ? 'Cancel' : 'Post'}
          </button>`}
      </div>

      ${writing && html`<${Write} onPut=${put} />`}
      ${error && html`<p class="error" data-testid="bulletin-error">
                        ${error}
                      </p>`}

      ${board.length === 0
        ? html`<p class="empty" data-testid="bulletin-empty">
                 Nothing on the board.
               </p>`
        : board.map((b) => html`
          <div key=${b.bulletin_id} class=${`bulletin ${b.kind}`}
               data-testid=${`bulletin-${b.bulletin_id}`}>
            <div class="head">
              <h4 data-testid="bulletin-title">${b.title}</h4>
              <span class="tag" data-testid="bulletin-kind">
                ${b.kind === 'event' ? 'EVENT' : 'NOTICE'}
              </span>
            </div>
            ${b.kind === 'event' && b.at && html`
              <div class="whenat" data-testid="bulletin-when">
                ${occasion(b.at)}
              </div>`}
            ${b.body && html`<div data-testid="bulletin-body">${b.body}</div>`}
            <div class="sub" data-testid="bulletin-by">
              ${`Posted by ${b.posted_by_name || 'the venue'}`}
            </div>
            <div class="row">
              <button class="secondary"
                      data-testid=${`bulletin-thread-${b.bulletin_id}`}
                      onClick=${() => setOpen(open === b.bulletin_id
                        ? null : b.bulletin_id)}>
                ${open === b.bulletin_id ? 'Hide talk' : 'Talk about it'}
              </button>
              ${mayPost && html`
                <button class="secondary"
                        data-testid=${`bulletin-remove-${b.bulletin_id}`}
                        onClick=${() => take(b.bulletin_id)}>Take down</button>`}
            </div>
            ${open === b.bulletin_id && html`
              <${Chat} title="About this"
                       base=${`${at}/${encodeURIComponent(b.bulletin_id)}`}
                       me=${me} />`}
          </div>`)}
    </div>`;
}

/**
 * Writing one.
 *
 * The kind is two buttons rather than a dropdown, because it changes what the
 * form asks for next and a change you can see is worth the width.
 */
function Write({ onPut }) {
  const [kind, setKind] = useState('note');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [when, setWhen] = useState('');

  const ready = title.trim() && (kind === 'note' || when);

  return html`
    <div class="card stack" data-testid="bulletin-form">
      <div class="ways">
        ${[['note', 'Notice'], ['event', 'Event']].map(([id, label]) => html`
          <button key=${id} class=${kind === id ? '' : 'secondary'}
                  data-testid=${`bulletin-kind-${id}`}
                  onClick=${() => setKind(id)}>${label}</button>`)}
      </div>
      <input data-testid="bulletin-title-input" value=${title} maxlength="120"
             placeholder=${kind === 'event' ? 'Quiz night' : 'Table recovered'}
             onInput=${(e) => setTitle(e.target.value)} />
      ${kind === 'event' && html`
        <label>When</label>
        <input type="datetime-local" data-testid="bulletin-when-input"
               value=${when} onInput=${(e) => setWhen(e.target.value)} />`}
      <textarea data-testid="bulletin-body-input" rows="3" maxlength="1000"
                placeholder="Anything else worth saying"
                value=${body}
                onInput=${(e) => setBody(e.target.value)}></textarea>
      <button data-testid="bulletin-post" disabled=${!ready}
              onClick=${() => onPut({
                kind, title: title.trim(), body: body.trim(),
                at: kind === 'event' ? when : null,
              })}>Put it up</button>
    </div>`;
}

/** The day and the hour — an event nobody can be late for is not an event. */
function occasion(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    weekday: 'short', day: 'numeric', month: 'short',
    hour: '2-digit', minute: '2-digit',
  });
}
